import { adminFetch } from "./base";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toQueryString(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v != null && v !== "",
  );
  if (entries.length === 0) return "";
  const qs = new URLSearchParams(entries.map(([k, v]) => [k, String(v)]));
  return `?${qs.toString()}`;
}

// ---------------------------------------------------------------------------
// Revenue types
// ---------------------------------------------------------------------------

export type RevenuePeriod = "day" | "week" | "month" | "quarter" | "year";

export interface RevenuePoint {
  readonly date: string;
  readonly revenue_fcfa: number;
  readonly orders: number;
  readonly avg_order_fcfa: number;
}

export interface RevenueKPIs {
  readonly total_fcfa: number;
  readonly growth_percent: number;
  readonly orders_count: number;
  readonly avg_order_fcfa: number;
  readonly top_region: string;
  readonly top_product: string;
}

export interface RevenueResponse {
  readonly kpis: RevenueKPIs;
  readonly series: readonly RevenuePoint[];
  readonly by_region: readonly { readonly region: string; readonly amount_fcfa: number }[];
  readonly by_brand: readonly { readonly brand: string; readonly amount_fcfa: number }[];
}

// ---------------------------------------------------------------------------
// TVA types
// ---------------------------------------------------------------------------

export type TVAStatus = "draft" | "submitted" | "paid";

export interface TVARate {
  readonly id: string;
  readonly name: string;
  readonly rate_percent: number;
  readonly applies_to: string;
}

export interface TVAEntry {
  readonly id: string;
  readonly period: string;
  readonly taxable_base_fcfa: number;
  readonly tva_collected_fcfa: number;
  readonly tva_deductible_fcfa: number;
  readonly tva_due_fcfa: number;
  readonly status: TVAStatus;
}

export interface TVAListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly period?: string | null;
  readonly status?: TVAStatus | null;
}

export interface TVAReport {
  readonly entries: readonly TVAEntry[];
  readonly total_due_fcfa: number;
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
  };
}

// ---------------------------------------------------------------------------
// Margins types
// ---------------------------------------------------------------------------

export interface MarginProduct {
  readonly id: string;
  readonly sku: string;
  readonly name: string;
  readonly brand: string;
  readonly sell_price_fcfa: number;
  readonly cost_price_fcfa: number;
  readonly margin_fcfa: number;
  readonly margin_percent: number;
  readonly units_sold: number;
  readonly revenue_fcfa: number;
}

export interface MarginListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly q?: string | null;
  readonly brand?: string | null;
}

export interface MarginResponse {
  readonly products: readonly MarginProduct[];
  readonly avg_margin_percent: number;
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
  };
}

// ---------------------------------------------------------------------------
// Expenses types
// ---------------------------------------------------------------------------

export type ExpenseCategory =
  | "transport"
  | "salaries"
  | "marketing"
  | "operations"
  | "other";

export type ExpenseStatus = "pending" | "approved" | "rejected";

export interface Expense {
  readonly id: string;
  readonly description: string;
  readonly category: ExpenseCategory;
  readonly amount_fcfa: number;
  readonly date: string;
  readonly approved_by: string | null;
  readonly status: ExpenseStatus;
  readonly receipt_url: string | null;
}

export interface ExpenseListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly category?: ExpenseCategory | null;
  readonly status?: ExpenseStatus | null;
  readonly date_from?: string | null;
  readonly date_to?: string | null;
}

export interface ExpenseListResponse {
  readonly expenses: readonly Expense[];
  readonly total_fcfa: number;
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
  };
}

export interface CreateExpenseInput {
  readonly description: string;
  readonly category: ExpenseCategory;
  readonly amount_fcfa: number;
  readonly date: string;
  readonly receipt_url?: string;
}

// ---------------------------------------------------------------------------
// Finance Invoice types
// ---------------------------------------------------------------------------

export type FinanceInvoiceStatus =
  | "draft"
  | "sent"
  | "paid"
  | "overdue"
  | "cancelled";

export interface FinanceInvoice {
  readonly id: string;
  readonly invoice_number: string;
  readonly client_name: string;
  readonly amount_ht_fcfa: number;
  readonly tva_fcfa: number;
  readonly amount_ttc_fcfa: number;
  readonly status: FinanceInvoiceStatus;
  readonly issued_at: string;
  readonly due_at: string;
  readonly paid_at: string | null;
}

export interface FinanceInvoiceListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly status?: FinanceInvoiceStatus | null;
  readonly date_from?: string | null;
  readonly date_to?: string | null;
  readonly q?: string | null;
}

export interface FinanceInvoiceListResponse {
  readonly invoices: readonly FinanceInvoice[];
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
  };
}

// ---------------------------------------------------------------------------
// Cashflow types
// ---------------------------------------------------------------------------

export interface CashflowEntry {
  readonly date: string;
  readonly inflows_fcfa: number;
  readonly outflows_fcfa: number;
  readonly net_fcfa: number;
  readonly balance_fcfa: number;
}

export interface CashflowResponse {
  readonly entries: readonly CashflowEntry[];
  readonly opening_balance_fcfa: number;
  readonly closing_balance_fcfa: number;
}

// ---------------------------------------------------------------------------
// Sage Export types
// ---------------------------------------------------------------------------

export type SageExportStatus = "pending" | "processing" | "completed" | "failed";

export interface SageExportJob {
  readonly id: string;
  readonly period: string;
  readonly status: SageExportStatus;
  readonly file_url: string | null;
  readonly created_at: string;
  readonly completed_at: string | null;
  readonly error: string | null;
}

export interface SageExportListResponse {
  readonly jobs: readonly SageExportJob[];
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
  };
}

// ---------------------------------------------------------------------------
// Reports types
// ---------------------------------------------------------------------------

export type ReportType = "revenue" | "tva" | "margin" | "cashflow" | "expenses";
export type ReportStatus = "generating" | "ready" | "failed";

export interface ReportTemplate {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly type: ReportType;
}

export interface GeneratedReport {
  readonly id: string;
  readonly template_id: string;
  readonly template_name: string;
  readonly period: string;
  readonly status: ReportStatus;
  readonly file_url: string | null;
  readonly created_at: string;
}

export interface ReportsListResponse {
  readonly templates: readonly ReportTemplate[];
  readonly recent: readonly GeneratedReport[];
}

// ---------------------------------------------------------------------------
// Daily P&L types
// ---------------------------------------------------------------------------

export interface DailyPnL {
  readonly date: string;
  readonly revenue_fcfa: number;
  readonly cogs_fcfa: number;
  readonly gross_profit_fcfa: number;
  readonly gross_margin_percent: number;
  readonly operating_expenses_fcfa: number;
  readonly ebitda_fcfa: number;
}

export interface DailyPnLResponse {
  readonly entries: readonly DailyPnL[];
  readonly totals: DailyPnL;
}

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

export const financeApi = {
  revenue: {
    get: (period: RevenuePeriod, dateFrom?: string, dateTo?: string) => {
      const qs = toQueryString({ period, date_from: dateFrom, date_to: dateTo });
      return adminFetch<RevenueResponse>(`/api/admin/finance/revenue${qs}`);
    },
  },

  tva: {
    list: (params: TVAListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit ?? 50,
        period: params.period,
        status: params.status,
      });
      return adminFetch<TVAReport>(`/api/admin/finance/tva${qs}`);
    },

    submit: (id: string) =>
      adminFetch<{ entry: TVAEntry }>(
        `/api/admin/finance/tva/${encodeURIComponent(id)}/submit`,
        { method: "PATCH" },
      ),
  },

  margins: {
    list: (params: MarginListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit ?? 50,
        q: params.q,
        brand: params.brand,
      });
      return adminFetch<MarginResponse>(`/api/admin/finance/margins${qs}`);
    },
  },

  expenses: {
    list: (params: ExpenseListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit ?? 50,
        category: params.category,
        status: params.status,
        date_from: params.date_from,
        date_to: params.date_to,
      });
      return adminFetch<ExpenseListResponse>(`/api/admin/finance/expenses${qs}`);
    },

    create: (input: CreateExpenseInput) =>
      adminFetch<{ expense: Expense }>("/api/admin/finance/expenses", {
        method: "POST",
        body: JSON.stringify(input),
      }),

    approve: (id: string) =>
      adminFetch<{ expense: Expense }>(
        `/api/admin/finance/expenses/${encodeURIComponent(id)}/approve`,
        { method: "PATCH" },
      ),

    reject: (id: string) =>
      adminFetch<{ expense: Expense }>(
        `/api/admin/finance/expenses/${encodeURIComponent(id)}/reject`,
        { method: "PATCH" },
      ),
  },

  invoices: {
    list: (params: FinanceInvoiceListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit ?? 50,
        status: params.status,
        date_from: params.date_from,
        date_to: params.date_to,
        q: params.q,
      });
      return adminFetch<FinanceInvoiceListResponse>(
        `/api/admin/finance/invoices${qs}`,
      );
    },
  },

  cashflow: {
    get: (dateFrom: string, dateTo: string) => {
      const qs = toQueryString({ date_from: dateFrom, date_to: dateTo });
      return adminFetch<CashflowResponse>(`/api/admin/finance/cashflow${qs}`);
    },
  },

  sage: {
    list: () =>
      adminFetch<SageExportListResponse>("/api/admin/finance/sage-exports"),

    trigger: (period: string) =>
      adminFetch<{ job: SageExportJob }>("/api/admin/finance/sage-exports", {
        method: "POST",
        body: JSON.stringify({ period }),
      }),
  },

  reports: {
    list: () =>
      adminFetch<ReportsListResponse>("/api/admin/finance/reports"),

    generate: (templateId: string, period: string) =>
      adminFetch<{ report: GeneratedReport }>("/api/admin/finance/reports", {
        method: "POST",
        body: JSON.stringify({ template_id: templateId, period }),
      }),
  },

  dailyPnl: {
    get: (dateFrom: string, dateTo: string) => {
      const qs = toQueryString({ date_from: dateFrom, date_to: dateTo });
      return adminFetch<DailyPnLResponse>(`/api/admin/finance/daily-pnl${qs}`);
    },
  },
} as const;
